import { Fragment, useMemo } from "react";
import { Grid, useMediaQuery } from "@mui/material";
import { useParams } from "react-router";

import { Breadcrumbs, PageHeader, ProgressOverlay } from "@dzambalaorg/mui-page-layout";
import { StyledEmptyWrapper, StyledPagination } from "@framework/styled";
import { type ITemplate, ModuleType, TokenType } from "@framework/types";
import { IPaginationResult } from "@dzambalaorg/types-collection";
import { useFetchQuery } from "@dzambalaorg/react-hooks";

import { TemplateSearchForm, TemplateListItem } from "../../../../components";

export interface ITemplateListProps {
  embedded?: boolean;
}

export const TemplateList = (props: ITemplateListProps) => {
  const { embedded } = props;

  const { id } = useParams<{ id: string }>();

  const initialSearchValues = useMemo(() => {
    return {
      contractIds: embedded ? [~~id!] : [],
    };
  }, [id]);

  const isMd = useMediaQuery(theme => theme.breakpoints.down("md"));

  const { isLoading, take, skip, onChangePage, hasNextPage, loadMoreRef, allRows, totalCount } = useFetchQuery<
    IPaginationResult<ITemplate>
  >("/templates", { enableInfiniteScroll: isMd, embedded }, void 0, initialSearchValues);

  return (
    <Fragment>
      <Breadcrumbs path={["dashboard", "marketplace", "marketplace.templates"]} isHidden={embedded} />

      <PageHeader message="pages.marketplace.templates.title" />

      <TemplateSearchForm
        initialValues={initialSearchValues}
        contractType={[TokenType.ERC721, TokenType.ERC998, TokenType.ERC1155]}
        contractModule={[ModuleType.HIERARCHY]}
        embedded={embedded}
      />

      <ProgressOverlay isLoading={isLoading}>
        <Grid container spacing={2}>
          <StyledEmptyWrapper count={allRows.length} isLoading={isLoading}>
            {allRows.map(template => (
              <Grid size={{ lg: 4, sm: 6, xs: 12 }} key={template.id}>
                <TemplateListItem template={template} path="/marketplace/templates" />
              </Grid>
            ))}
          </StyledEmptyWrapper>
        </Grid>
      </ProgressOverlay>

      {!isMd && totalCount > take && (
        <StyledPagination
          shape="rounded"
          page={skip / take + 1}
          count={Math.ceil(totalCount / take)}
          onChange={onChangePage}
        />
      )}
      {isMd && hasNextPage && <div ref={loadMoreRef} style={{ height: "20px", margin: "20px 0" }} />}
    </Fragment>
  );
};
